import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AANMELD_URL, api } from '@/lib/api';
import type { AuthStatus } from '@/lib/api-types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lock, ShieldAlert } from 'lucide-react';

/**
 * Aanmelden als beheerder, met Entra ID.
 *
 * Er is hier geen formulier meer. De vorige versie had een e-mail- en
 * wachtwoordveld en riep `supabase.auth.signInWithPassword` aan; wachtwoorden
 * werden dus door deze applicatie beheerd, zonder MFA en zonder een plek waar
 * iemand een account kon intrekken.
 *
 * Nu handelt App Service-authenticatie de aanmelding af, vóórdat een verzoek de
 * applicatie bereikt. Wat er hier gebeurt is een omleiding naar
 * /.auth/login/aad -- en dat pad wordt door het platform bediend, niet door deze
 * SPA. Vandaar `window.location` en geen react-router: dit is geen route in de
 * applicatie.
 *
 * De pagina blijft bestaan omdat er drie toestanden zijn die uitleg verdienen:
 * niet aangemeld, aangemeld zonder rechten, en aangemeld met rechten (dan hoort
 * niemand hier te zijn en volgt een doorverwijzing).
 */
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<AuthStatus | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const antwoord = await api.get<AuthStatus>('/auth/status');
        if (antwoord.isAdmin) {
          navigate('/admin', { replace: true });
          return;
        }
        setStatus(antwoord);
      } catch {
        // Zonder antwoord van de server is de aanmeldknop nog steeds het juiste
        // aanbod: het platform bedient dat pad, niet deze applicatie.
        setStatus({ aangemeld: false, isAdmin: false, ontwikkelmodus: false });
      }
    })();
  }, [navigate]);

  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /**
   * Aangemeld, maar geen beheerder.
   *
   * Een aparte boodschap, want dit is niet hetzelfde als "verkeerd wachtwoord": het
   * account bestaat en Entra heeft het geaccepteerd, maar er is geen rol. Iemand die
   * dit ziet moet worden uitgenodigd, niet nog een keer inloggen.
   */
  if (status.aangemeld && !status.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Geen beheerdersrechten
            </CardTitle>
            <CardDescription>
              U bent aangemeld{status.email ? ` als ${status.email}` : ''}, maar dit account heeft geen
              beheerdersrechten.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Vraag een beheerder om u uit te nodigen. Dat gaat op e-mailadres; de koppeling met uw
              account wordt bij uw volgende aanmelding automatisch gelegd.
            </p>
            {status.afmeldUrl && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.location.href = status.afmeldUrl!;
                }}
              >
                Afmelden
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" />
            Beheer
          </CardTitle>
          <CardDescription>Aanmelden met uw iSOFT-account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            className="w-full"
            onClick={() => {
              // Het platform bedient dit pad; een react-router-navigatie zou hier
              // een 404 in de SPA opleveren in plaats van een omleiding naar Entra.
              window.location.href = status.aanmeldUrl ?? AANMELD_URL;
            }}
          >
            Aanmelden met Microsoft
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            U wordt doorgestuurd naar Microsoft Entra ID. Wachtwoorden worden niet door deze
            applicatie beheerd.
          </p>
          {status.ontwikkelmodus && (
            <p className="text-xs text-muted-foreground text-center border-t pt-3">
              Ontwikkelomgeving: er is geen App Service-authenticatie. De beheerroutes gebruiken
              <code className="mx-1 text-[11px]">DEV_ADMIN_USER_ID</code>; in productie is die weg
              dicht.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
